import { ApiError, createIdempotencyKey, currentWorkflowEtag, request } from "./api";
import type { PlannerRule, PlannerRulePage, PlannerRuleRevision, SavePlannerRule } from "./plannerRules";
import type { InterpretPlannerRuleInput, PlannerRuleDraftContext, PlannerRuleInterpretationResponse } from "./plannerRuleDrafts";
import type { PlannerRuleProposal, PlannerRuleProposalCheck, PlannerRuleProposalEvent } from "./plannerRuleProposals";

const base = "/api/planner/rules";
export const plannerRulesApi = {
  list: (offset = 0) => request<PlannerRulePage>(`${base}?limit=50&offset=${offset}`),
  context: (projectId = "") => request<PlannerRuleDraftContext>(`${base}/context${projectId ? `?projectId=${encodeURIComponent(projectId)}` : ""}`),
  history: (id: string, offset = 0) => request<{ items: PlannerRuleRevision[]; total: number }>(`${base}/${id}/history?limit=20&offset=${offset}`),
  interpret: (input: InterpretPlannerRuleInput, key: string) => request<PlannerRuleInterpretationResponse>(`${base}/interpret`,
    { method: "POST", headers: { "Idempotency-Key": key }, body: JSON.stringify(input) }),
};

const proposals = `${base}/proposals`;
export const plannerRuleProposalsApi = {
  list: (offset = 0) => request<{ items: PlannerRuleProposal[]; total: number; offset: number; limit: number }>(`${proposals}?limit=50&offset=${offset}`),
  get: (id: string) => request<{ proposal: PlannerRuleProposal; check: PlannerRuleProposalCheck | null; events: PlannerRuleProposalEvent[] }>(`${proposals}/${id}`),
  check: (id: string) => request<PlannerRuleProposalCheck>(`${proposals}/checks/${id}`),
  export: (id: string) => request<Record<string, unknown>>(`${proposals}/${id}/export`),
  generate: (input: InterpretPlannerRuleInput, key: string) => request<PlannerRuleProposal>(proposals,
    { method: "POST", headers: { "Idempotency-Key": key }, body: JSON.stringify(input) }),
};
export function plannerRuleProposalCommand(action: "validate" | "approve" | "reject", proposal: PlannerRuleProposal, checkId?: string) {
  const key = createIdempotencyKey(), etag = currentWorkflowEtag("planner-rules"), body = JSON.stringify({ expectedVersion: proposal.version, ...(action === "approve" ? { checkId } : {}) });
  if (!etag) throw new ApiError("운영 규칙을 다시 불러와 주세요.", 428);
  return () => request<PlannerRuleProposal | PlannerRuleProposalCheck>(`${proposals}/${proposal.id}/${action}`,
    { method: "POST", body, headers: { "Idempotency-Key": key, "If-Match": etag } });
}

/** A retry keeps the exact key, body and ETag, including after an unconfirmed response. */
export function plannerRuleCommand(action: "create" | "update" | "approve" | "disable", rule?: PlannerRule,
  input?: SavePlannerRule, interpretationId?: string) {
  const key = createIdempotencyKey(), etag = currentWorkflowEtag("planner-rules");
  if (!etag) throw new ApiError("운영 규칙을 다시 불러와 주세요.", 428);
  const path = action === "create" ? base : action === "update" ? `${base}/${rule!.id}` : `${base}/${rule!.id}/${action}`;
  const body = JSON.stringify(action === "create" ? { ...input, interpretationId } : action === "update"
    ? { expectedVersion: rule!.version, rule: input, interpretationId } : { expectedVersion: rule!.version });
  return () => request<PlannerRule>(path, { method: action === "update" ? "PUT" : "POST", body,
    headers: { "Idempotency-Key": key, "If-Match": etag } });
}
